import { Box, Grid, MenuItem, Typography } from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CustomButton, CustomSelect } from '../../../v2/MUI_custom_component';
import { useLocation, useNavigate } from 'react-router-dom';
import { BillingInfo, LogisticDetails, ShippingAddress } from '../../Components';
import {
  comparePricesBtwPricingGeoAction,
  getpricingGeographyAction,
} from './API/Action';
import { selectDpRouteSource, selectGeneralCart, selectReferencePricingGeography, selectShowPriceChange } from './reducer/DpSelectors';
import { 
  moveProductsToShipToParty, 
  setShipToPartyDetails, 
  selectShipToParty,
  setReferencePricingGeography,
  resetReferencePricingGeography,
  showPriceChange
} from './reducer/DpActions';
import DisclaimerSvg from '../../Illustrration/components/DisclaimerSvg';
import HumbeeModal from '../../../v2/MUI_custom_component/Modal/HumbeeModal';
import { OperationType } from '../../../utils/settings';
import { ModifyOrderAction } from './API/Action';
import { buildModifyOrderPayload, buildProductInfo } from '../ModifyOrder';
import { DP_ROUTE_SOURCE } from './Enums';

const ShippingInfo = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedSupplier: selectedCompany ,selectedEntity: selectedCompanyEntity } = useSelector(
    (state) => state.entityReducer
  );
  const { order_booking_id } = useSelector((state) => {
      return {
        order_booking_id: state.dpReducer?.order_booking_id,
      };
  });
  const [ differentPricingGeo, setDifferentPricingGeo ] = useState(false);
  const generalCart = useSelector(selectGeneralCart);
  const referencePricingGeo = useSelector(selectReferencePricingGeography);
  
  const shipToPartyCarts = useSelector(state => state.dpReducer?.shipToPartyCarts || {});
  const shipToPartyCount = Object.keys(shipToPartyCarts).length;
  
  const {
    buyerDetails = {},
    influencerTabSelected = false,
    selectedRole = '',
    editMode = false,
    shipToPartyData,
    shipToPartyKey = null,
    STPCountMore = false,
  } = state || {};

  // Stable cart key for this STP: existing entries keep their `so_<humbee_so_id>`
  // key across refetches; brand-new STPs get a one-time temp key.
  const generatedKeyRef = useRef(null);
  const resolveShipToPartyKey = () => {
    if (shipToPartyKey) return shipToPartyKey;
    if (buyer.humbee_so_id) return `so_${buyer.humbee_so_id}`;
    if (!generatedKeyRef.current) {
      generatedKeyRef.current = `tmp_${Date.now()}`;
    }
    return generatedKeyRef.current;
  };

  const esiOptions = React.useMemo(() => {
    if (buyerDetails?.esi_info_with_operational_address?.length) {
      return buyerDetails.esi_info_with_operational_address.map(item => ({
        value: item.esi_info.esi_id,
        label: item.esi_info.external_system_code,
        address: item.address
      }));
    }
    if (editMode && shipToPartyData?.selected_esi_info) {
      return [
        {
          value: shipToPartyData.selected_esi_info.esi_id,
          label: shipToPartyData.selected_esi_info.external_system_code,
          address: null
        }
      ];
    }
    return [];
  }, [buyerDetails, editMode, shipToPartyData]);

  const defaultEsiId = editMode
    ? shipToPartyData?.selected_esi_info?.esi_id ?? null
    : esiOptions[0]?.value ?? null;

  const defaultEsi =
  esiOptions.find(e => e.address?.is_default) || esiOptions[0] || null;
  const [buyer, setBuyer] = useState({
    ...(editMode ? shipToPartyData : buyerDetails),
    selected_esi_id: editMode
      ? shipToPartyData?.selected_esi_info?.esi_id ?? null
      : defaultEsi?.value ?? null,

    selected_esi_info: editMode
      ? shipToPartyData?.selected_esi_info
      : defaultEsi
      ? {
          esi_id: defaultEsi.value,
          external_system_code: defaultEsi.label
        }
      : null,
    selectedRole,
  });
  const [selectedEsiId, setSelectedEsiId] = useState(defaultEsiId);
  const [pricingGeographyList, setPricingGeographyList] = useState([]);
  const [currentPricingGeo] = useState(editMode
            ? shipToPartyData.pricing_geography_id
            : buyer.pricing_geography_id);
  const [updatedPricingGeo, setUpdatedPricingGeo] = useState(null);
  const [showPriceComparisonPopup, setShowPriceComparisonPopup] = useState(false);
  const [showUpdateOrderModal, setShowUpdateOrderModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialDistrictId] = useState(editMode
            ? shipToPartyData?.delivery_district_id
            : buyer.delivery_district_id);
  const [hasDistrictChanged, setHasDistrictChanged] = useState(false);
  const handleUpdateOrder = () => {
    setShowUpdateOrderModal(false);
    updateShipToParty();
  };
  useEffect(() => {
    if (!editMode || !shipToPartyData) return;

    const esiId = shipToPartyData?.selected_esi_info?.esi_id ?? null;

    setSelectedEsiId(esiId);
    setBuyer(prev => ({
      ...prev,
      selected_esi_id: esiId,
      selected_esi_info: shipToPartyData?.selected_esi_info
    }));
  }, [editMode, shipToPartyData]);

  useEffect(() => {
    if (editMode) return;
    if (!selectedEsiId) return;

    const selectedEsi = esiOptions.find(e => e.value === selectedEsiId);
    if (!selectedEsi?.address) return;

    const {
      address_info = {},
      pincode_info = {},
      district_info = {},
      state_info = {},
      location_info = {}
    } = selectedEsi.address;

    setBuyer(prev => ({
      ...prev,
      selected_esi_id: selectedEsi.value,
      selected_esi_info: {
        esi_id: selectedEsi.value,
        external_system_code: selectedEsi.label
      },
      delivery_Address_line1: address_info.address_line_1 || '',
      delivery_Address_line2: address_info.address_line_2 || '',
      // delivery_Address_line3: address_info.address_line_3 || '',
      delivery_company_pincode: pincode_info.pincode_id || null,
      delivery_district_id: district_info.district_id || null,
      delivery_district_name: district_info.district_name || null,
      delivery_state_id: state_info.state_id || null,
      delivery_state_name: state_info.state_name || null,
      delivery_location_id: location_info.location_id || null,
      delivery_location_name: location_info.location_name || ''
    }));
  }, [selectedEsiId, editMode, esiOptions]);

  const validateShippingAddress = () => {
    if (!buyer.company_id) return 'Company ID is missing';
    if (!buyer.delivery_state_id) return 'State is required';
    if (!buyer.delivery_district_id) return 'District is required';
    if (!buyer.delivery_location_id && !buyer.delivery_company_pincode)
      return 'City or Pincode is required';
    if (!buyer.delivery_Address_line1)
      return 'Address Line 1 is required';
    if (!buyer.delivery_Address_line2)
      return 'Address Line 2 is required';
    if (!buyer.delivery_type)
      return 'Delivery type is required';
    return null;
  };
  // Duplicate rule: same company + same address line 1 & 2. Compare loosely —
  // ids arrive as string or number depending on the API, and address strings
  // differ in case/whitespace between typed input and hydrated order data.
  const normalizeAddressLine = (value) =>
    (value ?? '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
  const isSameAddress = (a, b) => {
    return (
      String(a.company_id) === String(b.company_id) &&
      normalizeAddressLine(a.delivery_Address_line1) === normalizeAddressLine(b.delivery_Address_line1) &&
      normalizeAddressLine(a.delivery_Address_line2) === normalizeAddressLine(b.delivery_Address_line2)
    );
  };

  const isDuplicateShipToParty = () => {
    return Object.entries(shipToPartyCarts).some(([shipToPartyId, cartData]) => {
      if (editMode && shipToPartyId === resolveShipToPartyKey()) return false;
      const existingDetails = cartData?.shipToPartyDetails;
      if (!existingDetails) return false;
      return isSameAddress(buyer, existingDetails);
    });
  };

  const handleSaveShipToParty = async(pricingGeographyChange = false) => {
    const error = validateShippingAddress();
    if (error) {
      toast.error(error);
      return;
    }
    if (isDuplicateShipToParty()) {
      toast.error('Selected Ship To Party already added. Choose another or add products to the existing.');
      return;
    }
    let comparisonResult = false;
    if (shipToPartyCount >= 1 && updatedPricingGeo && referencePricingGeo.pricing_geography_id) {
      setDifferentPricingGeo(true);
      comparisonResult = await handlePricingGeographyChange(
        referencePricingGeo.pricing_geography_id,
        updatedPricingGeo.value,
      );
    }
    else if (pricingGeographyList.length && updatedPricingGeo) {
      comparisonResult = await handlePricingGeographyChange(
        null,
        updatedPricingGeo.value,
      );
    }
    if (!comparisonResult) {
      saveShipToParty();
    }
  };
  const routeSource = useSelector(selectDpRouteSource);
  const saveShipToParty = (pricingGeographyChange = false) => {
    const shipToPartyId = resolveShipToPartyKey();
    dispatch(setShipToPartyDetails(shipToPartyId, buyer));
    dispatch(selectShipToParty(shipToPartyId));

    // Store reference pricing geography for first STP or when pricing geo is updated
    if (shipToPartyCount === 0 || pricingGeographyChange) {
      const pricingGeoToStore = updatedPricingGeo || {
        value: currentPricingGeo || buyer.pricing_geography_id,
        label: buyer.pricing_geography_name
      };
      
      if (pricingGeoToStore.value) {
        dispatch(setReferencePricingGeography({
          pricing_geography_id: pricingGeoToStore.value,
        }));
      }
    }
    pricingGeographyChange && dispatch(showPriceChange(true));
    
    if (generalCart.length > 0) {
      dispatch(
        moveProductsToShipToParty(
          shipToPartyId,
          generalCart.map(p => p.product_id)
        )
      );
    }
    // Show modal for edit mode, navigate directly for new STP
    if (editMode && routeSource === DP_ROUTE_SOURCE.DP_DETAILS) {
      setShowUpdateOrderModal(true);
    } else {
      if(routeSource){
        navigate('/dp-order-summary', {
          state: {
            stpAction: OperationType.ADD,
            order_id,
            shipToPartyId,
            updatedPricingGeo: updatedPricingGeo
              ? { value: updatedPricingGeo.value, label: updatedPricingGeo.label }
              : null,
          }
        });
      }else{
        if (updatedPricingGeo){
          navigate('/dp-order-summary', {
            state: {
              shipToPartyId,
              order_id,
              updatedPricingGeo: updatedPricingGeo
                ? { value: updatedPricingGeo.value, label: updatedPricingGeo.label }
                : null,
            }
          });
          return
        } else{
          navigate('/dp-order-summary', {
            state: { 
              order_id,
              fromShipping: true,
              shipToPartyId 
            }
          });
        }
      }
    }
    
  };
  useEffect(() => {
    if (!buyer.delivery_district_id || !selectedCompany) return;
    if (initialDistrictId && buyer.delivery_district_id !== initialDistrictId) {
      setHasDistrictChanged(true);
    }
    
    getGeographyPricing({ district_id: buyer.delivery_district_id });
  }, [buyer.delivery_district_id, initialDistrictId, hasDistrictChanged]);

  const getGeographyPricing = async ({ district_id }) => {
    const params = new URLSearchParams();
    params.append('district_id', district_id);
    params.append('manufacturer_id', selectedCompany);

    const { data, error, message } = await getpricingGeographyAction(params);

    if (!error) {
      const geographyList = data.map((g) => ({
        label: g.pricing_geography_name,
        value: g.pricing_geography_id
      }));
      setPricingGeographyList(geographyList);

      if (geographyList.length) {
        const newPricingGeo = {
          value: geographyList[0].value,
          label: geographyList[0].label
        };
        setUpdatedPricingGeo(newPricingGeo);
        setBuyer(prev => ({
          ...prev,
          pricing_geography_id: newPricingGeo.value,
          pricing_geography_name: newPricingGeo.label
        }));
      }
    } else {
      toast.error(message);
    }
  };
  useEffect(() => {
    if (!selectedEsiId) return;
    const selectedEsi = esiOptions.find(e => e.value === selectedEsiId);
    if (!selectedEsi?.address) return;

    const {
      address_info = {},
      pincode_info = {},
      district_info = {},
      state_info = {},
      location_info = {}
    } = selectedEsi.address;

    setBuyer(prev => ({
      ...prev,
      selected_esi_info: {
        esi_id: selectedEsi.value,
        external_system_code: selectedEsi.label
      },
      delivery_Address_line1: address_info.address_line_1 || '',
      delivery_Address_line2: address_info.address_line_2 || '',
      // delivery_Address_line3: address_info.address_line_3 || '',
      delivery_company_pincode: pincode_info.pincode_id || null,
      delivery_district_id: district_info.district_id || null,
      delivery_district_name: district_info.district_name || null,
      delivery_state_id: state_info.state_id || null,
      delivery_state_name: state_info.state_name || null,
      delivery_location_id: location_info.location_id || null,
      delivery_location_name: location_info.location_name || ''
    }));
  }, [selectedEsiId]);
  const handlePricingGeographyChange = async (comparisonGeo, updatedGeo) => {
    if (!updatedGeo || updatedGeo === comparisonGeo) {
      return false;
    }
    
    const result = await priceComparison(comparisonGeo, updatedGeo);
    return result;
  };
  const handleChangePricingGeo = () => { 
    setShowPriceComparisonPopup(false);
    if (updatedPricingGeo) {
      dispatch(setReferencePricingGeography({
        pricing_geography_id: updatedPricingGeo.value,
      }));
      
    }
    // dispatch(showPriceChange(true));
    saveShipToParty(true);   
  }
  const priceComparison = async (updatedGeo, currentGeo) => {
    const payload = {
      pricing_geography_id_1: currentGeo,
      pricing_geography_id_2: updatedGeo,
      product_company_id: selectedCompany,
      company_esi_id: selectedCompanyEntity,
      order_booking_id: order_booking_id,
    };

    const { data, error, message } = await comparePricesBtwPricingGeoAction(payload);
    if (error) {
      toast.error(message);
      return false;
    }
    if (data.result !== true) {
      setShowPriceComparisonPopup(true);
      return true;
    }
    return false;
  };
  const { order_id } = useSelector((state) => {
    return {
      order_id: state.dpReducer?.order_id,
    };
  });

  const isDPDetailsRoute = routeSource === DP_ROUTE_SOURCE.DP_DETAILS;
  // Scenario flags
  const isFirstSTP = shipToPartyCount === 0 || shipToPartyCount === 1;
  const isAddingSTP = !editMode && shipToPartyCount > 1;
  const isEditingFirstSTP = editMode && isFirstSTP;
  const isAddingSecondOrMoreSTP = !editMode && shipToPartyCount > 1;
  const isEditingSTPMoreThanOne = editMode && shipToPartyCount > 1;
  // For clarity, use routeSource and editMode to distinguish

  let modalTitle = '';
  let modalSubtitle = '';
  let modalConfirmLabel = '';
  let modalCancelLabel = '';
  let modalConfirmHandler = null;
  let modalCloseHandler = null;

  // Case 1: New user - Create DP - adding 1st STP
  if (!editMode && shipToPartyCount === 0) {
    modalTitle = 'Price changes will occur for entered shipping address!';
    modalSubtitle = 'The final billing amount will change based on the entered shipping address.';
    modalConfirmLabel = 'Continue';
    modalCancelLabel = 'Change Address';
    modalConfirmHandler = handleChangePricingGeo;
    modalCloseHandler = () => setShowPriceComparisonPopup(false);
  }
  // Case 2: Editing First STP (Order Summary or DP Details)
  else if (editMode && (shipToPartyCount === 0 || shipToPartyCount === 1)) {
    modalTitle = 'Price changes will occur for entered shipping address!';
    modalSubtitle = 'The final billing amount will change based on the entered shipping address.';
    modalConfirmLabel = 'Continue';
    modalCancelLabel = 'Change Address';
    modalConfirmHandler = isDPDetailsRoute ? handleUpdateOrder : handleChangePricingGeo;
    modalCloseHandler = () => setShowPriceComparisonPopup(false);
  }
  // Case 3 & 4: DP Details - Adding 2nd or 3rd STP OR From Order Summary - Adding 2nd or 3rd STP
  else if (!editMode && shipToPartyCount > 0) {
    modalTitle = 'Selected Ship to Party falls in a different pricing geography.';
    modalSubtitle = 'Please select a different Ship to Party in the same pricing geography or create a new Dispatch Plan.';
    modalConfirmLabel = 'Edit Address';
    modalCancelLabel = 'Cancel';
    modalConfirmHandler = () => setShowPriceComparisonPopup(false);
    modalCloseHandler = () => {
      setShowPriceComparisonPopup(false);
      if (isDPDetailsRoute) {
        navigate(`/dispatch-plan-detail/${order_id}`);
      } else {
        navigate('/dp-order-summary', {
          state: {
            order_id,
            shipToPartyId: resolveShipToPartyKey(),
            fromShipping: true
          }
        });
      }
    };
  }
  // Case 5 & 6: STP > 1, From DP Details - Change address of any STP OR Order summary - Change Address (Edit mode) for any STP
  else if (editMode && shipToPartyCount > 1) {
    modalTitle = 'Entered Address falls in a different pricing geography.';
    modalSubtitle = 'Please edit address to match existing pricing geography or go to back to order details.';
    modalConfirmLabel = 'Edit Address';
    modalCancelLabel = 'Cancel';
    modalConfirmHandler = () => setShowPriceComparisonPopup(false);
    modalCloseHandler = () => {
      setShowPriceComparisonPopup(false);
      if (isDPDetailsRoute) {
        navigate(`/dispatch-plan-detail/${order_id}`);
      } else {
        navigate('/dp-order-summary', {
          state: {
            order_id,
            shipToPartyId: resolveShipToPartyKey(),
            fromShipping: true
          }
        });
      }
    };
  }
  // Fallback (should not be hit, but for safety)
  else {
    modalTitle = 'Price changes will occur for entered shipping address!';
    modalSubtitle = 'The final billing amount will change based on the entered shipping address.';
    modalConfirmLabel = 'Continue';
    modalCancelLabel = 'Change Address';
    modalConfirmHandler = isDPDetailsRoute ? handleUpdateOrder : handleChangePricingGeo;
    modalCloseHandler = () => setShowPriceComparisonPopup(false);
  }
  
  const updateShipToParty = async () => {
    if (!buyer?.company_id) {
      toast.error('Invalid Ship to Party');
      return;
    }
    
    // Get products from Redux for this specific ship-to-party
    const shipToPartyId = resolveShipToPartyKey();
    const currentShipToPartyCart = shipToPartyCarts[shipToPartyId]?.cart || [];
    // Backend requires the STP's products (with quantities) on an address
    // update: existing lines go as context (null operation), new ones as ADD
    const products = currentShipToPartyCart.map((p) =>
      buildProductInfo(
        p,
        p?.isExistingItem || p?.order_product_delivery_id ? null : OperationType.ADD
      )
    );

    const shipment = {
      ship_to_party_info: {
        humbee_so_id: buyer.humbee_so_id,
        company_info: {
          id: buyer.company_id,
          esi_id: buyer.selected_esi_id,
          external_system_code: buyer.selected_esi_info?.external_system_code,
          name: buyer.company_name,
          phone: buyer.company_phone_no,
          gst: buyer.gstin,
        },
        address_info: {
          address: {
            address_line_1: buyer.delivery_Address_line1,
            address_line_2: buyer.delivery_Address_line2,
            // address_line_3: buyer.delivery_Address_line3,
          },
          pincode: buyer.delivery_company_pincode,
          district_info: {
            id: buyer.delivery_district_id,
            name: buyer.delivery_district_name,
          },
          state_info: {
            id: buyer.delivery_state_id,
            name: buyer.delivery_state_name,
          },
          location_info: {
            location_id: buyer.delivery_location_id,
            location_name: buyer.delivery_location_name,
          },
        },
        receiver_info: {
          name: buyer.receiver_name ?? '',
          phone_number: buyer.receiver_mobile ?? '',
        },
        delivery_info: {
          delivery_type: buyer.delivery_type,
          incoterms: buyer.incoterms ?? 2,
        },
        pricing_geography_info: {
          pricing_geography_id: buyer.pricing_geography_id,
          pricing_geography_name: buyer.pricing_geography_name,
        },
      },
    };

    const payload = buildModifyOrderPayload({
      orderId: order_id,
      shipment,
      shipToPartyOperation: OperationType.UPDATE,
      products,
    });

    setLoading(true);
    const { error, message } = await ModifyOrderAction(
      payload,
      selectedCompanyEntity
    );
    setLoading(false);

    if (!error) {
      toast.success(message);
      navigate(`/dispatch-plan-detail/${order_id}`);
    } else {
      toast.error(message);
    }
  };

  
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent:'end', 
        display: 'flex', 
        justifyContent:'end', 
        p: { xs: 2, md: 0 },
        mr: { xs: 0, md: 4 },
        position: {xs: 'fixed', md: 'relative'}, 
        top: { xs: '88px', md: 0 }, 
        left: {xs: 0, md: 'auto'},
        right: {xs: 0, md: 'auto'},
        zIndex: {xs: 1000, md: 'auto'}, 
        zIndex: 1000, 
        boxShadow: {xs: '0px 2px 8px 0px #0000001A', md: 'none'},
        background: {xs: '#FAFAFA', md: 'transparent'},
        pb: 0,
      }}>
        <CustomButton
          variant="filled"
          color="primary"
          label="Continue"
          loading={loading}
          onClick={handleSaveShipToParty}
          sx={{
            width: { xs: "100%", md: "auto" },
          }}
        />
      </Box>

      <Box sx={{ mt: { xs: '72px', md: 0 }, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h2">Shipping Info</Typography>
        <Box sx={{ background: '#fff', p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <BillingInfo
            buyer={buyer}
            selectedRole={selectedRole}
            title="Ship to Party"
            showRoleChip
            orderSummary={false}
            showErpCode={esiOptions.length === 0}
          />

          {esiOptions.length > 0 && (
            <CustomSelect
              width={{ xs: "100%", md: "70%" }}
              label="ERP Code"
              value={selectedEsiId}
              onChange={(e) => setSelectedEsiId(e.target.value)}
            >
              {esiOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </CustomSelect>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
          <Box sx={{ background: '#fff', p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <ShippingAddress
                buyer={buyer}
                setBuyer={setBuyer}
                influencerTabSelected={influencerTabSelected}
              />
          </Box>
          </Grid>
          <Grid item xs={12} md={6}>
          <Box sx={{ background: '#fff', p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <LogisticDetails
                selectedDeliveryType={buyer.delivery_type}
                onDeliveryTypeChange={(v) =>
                  setBuyer(prev => ({ ...prev, delivery_type: v }))
                }
                receiverName={buyer.receiver_name}
                onReceiverNameChange={(v) =>
                  setBuyer(prev => ({ ...prev, receiver_name: v }))
                }
                receiverMobile={buyer.receiver_mobile}
                onReceiverMobileChange={(v) =>
                  setBuyer(prev => ({ ...prev, receiver_mobile: v }))
                }
              />
          </Box>
          </Grid>
        </Grid>
      </Box>
      <HumbeeModal
        open={showPriceComparisonPopup}
        onClose={modalCloseHandler}
        onConfirm={modalConfirmHandler}
        title={modalTitle}
        subtitle={modalSubtitle}
        confirmLabel={modalConfirmLabel}
        cancelLabel={modalCancelLabel}
        loading={loading}
        illustration={<DisclaimerSvg />}
        width={{ xs: 400 , md:  600 }}
        buttonSx={{
          width: { xs: 'auto', md: '100%' },
          flex: { xs: 'unset', md: 1 }
        }}
        // buttonSx={{ width: { xs: 'auto', md: '100%' } }}
      />
      
      <HumbeeModal
        open={showUpdateOrderModal}
        onClose={() => setShowUpdateOrderModal(false)}
        onConfirm={handleUpdateOrder}
        loading={loading}
        title="Address updated for Ship to Party"
        subtitle="Your order will be updated."
        confirmLabel="Update Order"
        cancelLabel="No"
        illustration={<DisclaimerSvg />}
        width={{ xs: 400, md: 400 }}
      />
    </>
  );
};

export default ShippingInfo;
